import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import Home from "./pages/Home";
import PageSEO from "./components/PageSEO";

const ChatWidgetBundle = lazy(() => import("./components/ChatWidgetBundle"));
const PricingPage = lazy(() => import("./pages/PricingPage"));
const ResourcesPage = lazy(() => import("./pages/ResourcesPage"));
const GlossaryPage = lazy(() => import("./pages/GlossaryPage"));
const GlossaryTermPage = lazy(() => import("./pages/GlossaryTermPage"));
const ComparePage = lazy(() => import("./pages/ComparePage"));
const ComparisonPage = lazy(() => import("./pages/ComparisonPage"));
const AskAnterUseCasePage = lazy(() => import("./pages/AskAnterUseCasePage"));
const ChatSdkPage = lazy(() => import("./pages/ChatSdkPage"));
const ResourcesArticlesPage = lazy(() => import("./pages/ResourcesArticlesPage"));
const ResourcesArticlePage = lazy(() => import("./pages/ResourcesArticlePage"));
const WhyAnterPage = lazy(() => import("./pages/WhyAnterPage"));
const WatchDemoPage = lazy(() => import("./pages/WatchDemoPage"));
const PrivacyPage = lazy(() => import("./pages/legal/PrivacyPage"));
const TermsPage = lazy(() => import("./pages/legal/TermsPage"));
const CookiePolicyPage = lazy(() => import("./pages/legal/CookiePolicyPage"));
const AiPolicyPage = lazy(() => import("./pages/legal/AiPolicyPage"));
const McpAgentPlatformPage = lazy(() => import("./pages/solutions/McpAgentPlatformPage"));

export default function App() {
  const [pathname, setPathname] = useState(() => window.location.pathname);

  useEffect(() => {
    const openChat = () => {
      const trigger = document.getElementById("ask-anter-trigger");
      if (!trigger) return;
      const label = trigger.getAttribute("aria-label");
      if (label === "Ask Anter") {
        trigger.click();
      }
    };

    window.addEventListener("anter:open-chat", openChat);
    return () => window.removeEventListener("anter:open-chat", openChat);
  }, []);

  useEffect(() => {
    const onPopState = () => setPathname(window.location.pathname);
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  // Start each route at the top, unless navigating to an in-page anchor.
  // Move focus to main so screen-reader users land on the new page (WCAG 2.4.3).
  useEffect(() => {
    if (!window.location.hash) window.scrollTo(0, 0);
    document.getElementById("main-content")?.focus();
  }, [pathname]);

  useEffect(() => {
    const handleLinkClick = (e: MouseEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey || e.shiftKey || e.button !== 0) return;

      const target = (e.target as HTMLElement).closest("a");
      if (!target) return;

      const href = target.getAttribute("href");
      if (
        !href ||
        href.startsWith("http") ||
        href.startsWith("//") ||
        href.startsWith("#") ||
        href.startsWith("mailto:")
      )
        return;

      const url = new URL(href, window.location.origin);
      const p = url.pathname === "/" ? "/" : url.pathname.replace(/\/+$/, "");

      const isSpaRoute =
        p === "/" ||
        p.startsWith("/glossary") ||
        p.startsWith("/compare") ||
        p.startsWith("/resources") ||
        [
          "/pricing",
          "/design-partners",
          "/partners",
          "/why-anter",
          "/watch/anter-product-demo",
          "/solutions/mcp-agent-platform",
          "/chat-sdk",
          "/privacy",
          "/terms",
          "/cookies",
          "/ai-policy",
        ].includes(p);

      if (isSpaRoute) {
        e.preventDefault();
        window.history.pushState({}, "", href);
        window.dispatchEvent(new PopStateEvent("popstate"));
      }
    };

    document.addEventListener("click", handleLinkClick);
    return () => document.removeEventListener("click", handleLinkClick);
  }, []);

  const page = useMemo(() => {
    const normalizedPath = pathname === "/" ? "/" : pathname.replace(/\/+$/, "");

    if (normalizedPath === "/glossary") {
      return <GlossaryPage />;
    }

    if (normalizedPath.startsWith("/glossary/")) {
      const slug = normalizedPath.replace(/^\/glossary\//, "");
      return <GlossaryTermPage slug={slug} />;
    }

    if (normalizedPath === "/compare") {
      return <ComparePage />;
    }

    if (normalizedPath.startsWith("/compare/")) {
      const slug = normalizedPath.replace(/^\/compare\//, "");
      return <ComparisonPage slug={slug} />;
    }

    if (normalizedPath === "/resources/articles") {
      return <ResourcesArticlesPage />;
    }

    if (normalizedPath.startsWith("/resources/articles/")) {
      const slug = normalizedPath.replace(/^\/resources\/articles\//, "");
      return <ResourcesArticlePage slug={slug} />;
    }

    switch (normalizedPath) {
      case "/pricing":
      case "/design-partners":
      case "/partners":
        return <PricingPage />;
      case "/why-anter":
        return <WhyAnterPage />;
      case "/watch/anter-product-demo":
        return <WatchDemoPage />;
      case "/resources":
        return <ResourcesPage />;
      case "/resources/ask-anter":
        return <AskAnterUseCasePage />;
      case "/solutions/mcp-agent-platform":
        return <McpAgentPlatformPage />;
      case "/chat-sdk":
        return <ChatSdkPage />;
      case "/privacy":
        return <PrivacyPage />;
      case "/terms":
        return <TermsPage />;
      case "/cookies":
        return <CookiePolicyPage />;
      case "/ai-policy":
        return <AiPolicyPage />;
      case "/":
        return <Home />;
      default:
        return (
          <div className="min-h-screen bg-bg text-text flex flex-col items-center justify-center p-6 text-center">
            <a href="#main-content" className="skip-link">
              Skip to main content
            </a>
            <PageSEO
              title="404 — Page Not Found | Anter"
              description="The page you requested could not be found."
              canonical=""
              noindex={true}
            />
            <main id="main-content" tabIndex={-1}>
              <h1 className="text-5xl font-extrabold text-primary mb-4">404</h1>
              <p className="text-lg text-text-muted mb-6">
                The page you requested could not be found.
              </p>
              <a
                href="/"
                className="inline-flex items-center gap-2 rounded-full border border-primary bg-primary px-6 py-3 text-sm font-bold text-primary-text-strong transition hover:opacity-90"
              >
                Back to Anter Homepage
              </a>
            </main>
          </div>
        );
    }
  }, [pathname]);

  return (
    <>
      <Suspense fallback={null}>{page}</Suspense>
      <Suspense fallback={null}>
        <ChatWidgetBundle />
      </Suspense>
    </>
  );
}
